import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    OnInit,
    Signal,
    signal,
    WritableSignal
} from "@angular/core";
import {LoadingStatus} from '../../session/loading-status';
import {LoadingService} from "../../ui-elements/loading/loading.service";
import {AnalyticsService} from '../../logging/analytics.service';
import {Page} from "../../navigation/page";
import {IdentityModel} from '../../identity/identity.model';
import {LoyaltyCardData} from '../../identity/member-summary-entity.model';
import {MessageDetails, MessageLevel} from "../../notification/message/message";
import {NotificationService} from "../../notification/notification.service";
import {CheckboxSpec} from "../../notification/simple-dialog-page/simple-dialog-page.component";
import {DealData} from '../../deal/deal-entity.model';
import {DealModel, DealResult, DealResultCode} from '../../deal/deal.model';
import {EntityData} from "@fedelta-pos/data-client";
import {ModalPageLayoutComponent} from '../../page-layout/modal-page-layout/modal-page-layout.component';
import {TicketComponent} from "../ticket/ticket.component";
import {LoyaltyCardComponent} from "../loyalty-card/loyalty-card.component";
import {LinkComponent} from '../../buttons/link/link.component';

@Component({
    selector: "woa-loyalty-cards-page",
    templateUrl: "./loyalty-cards-page.component.html",
    styleUrl: "./loyalty-cards-page.component.scss",
    providers: [{provide: Page, useExisting: LoyaltyCardsPageComponent}],
    imports: [
        LinkComponent,
        LoyaltyCardComponent,
        ModalPageLayoutComponent,
        TicketComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoyaltyCardsPageComponent extends Page implements OnInit {
    private identityModel: IdentityModel = inject(IdentityModel);
    private dealModel: DealModel = inject(DealModel);
    private loadingService: LoadingService = inject(LoadingService);
    private analyticsService: AnalyticsService = inject(AnalyticsService);
    private notificationService: NotificationService = inject(NotificationService);

    public LoadingStatus = LoadingStatus;

    public static readonly ENABLE_TICKETS: boolean = false;

    public readonly status: WritableSignal<LoadingStatus> = signal<LoadingStatus>(LoadingStatus.LOADING);
    public readonly tickets: WritableSignal<DealData[]> = signal<DealData[]>([]);
    public readonly loyaltyCards: WritableSignal<LoyaltyCardData[]> = signal<LoyaltyCardData[]>([]);

    public readonly contentText: Signal<string | undefined> = computed<string | undefined>(() =>
        this.showCards() || this.status() !== LoadingStatus.LOADED ? undefined : "woa.message.loyalty-cards.noCards");

    public readonly showCards: Signal<boolean> = computed<boolean>(() =>
        this.status() === LoadingStatus.LOADED && !this.identityModel.isGuestUser() && !!this.identityModel.currentMember() &&
        (!!this.tickets().length || !!this.loyaltyCards().length));

    ngOnInit(): void {
        this.status.set(LoadingStatus.LOADING);
        this.loadMemberSummary();
    }

    loadMemberSummary(): void {
        void this.loadingService.performWithSpinner(async () => {
            try {
                await this.identityModel.loadMemberSummary();
                this.status.set(LoadingStatus.LOADED);
            } catch (error) {
                this.status.set(LoadingStatus.FAILED);
            }
            this.tickets.set(LoyaltyCardsPageComponent.ENABLE_TICKETS ? this.identityModel.tickets() : []);
            this.loyaltyCards.set(this.identityModel.loyaltyCards());
        });
    }

    async profile(): Promise<void> {
        void this.analyticsService.trackNavOption("profile");
        void this.dismiss();
        return this.navigationService.createPage("woa-profile-edit-page");
    }

    async redeemCard(card: LoyaltyCardData): Promise<void> {
        const applyDeal: CheckboxSpec = {
            label: "woa.message.loyalty-cards.autoApplyDeal",
            name: "applyDeal",
            checked: true
        };

        if (await this.notificationService.displayConfirmation("woa.heading.loyalty-cards.confirmRedeemLoyaltyCard", undefined,
            "woa.message.loyalty-cards.confirmRedeemLoyaltyCard",
            "woa.button.common.noThanks", "woa.button.loyalty-cards.confirmRedeem", false,
            {points: card.pointsRequiredToRedeem}, false,
            [applyDeal])) {
            const redemptionResult: unknown = await this.loadingService.performWithSpinner(async () => await this.dealModel.redeemLoyaltyCard(card));
            this.loadMemberSummary();
            if (redemptionResult != undefined) {
                if (applyDeal.checked) {
                    const deal: DealData = <DealData>EntityData.create(DealData).assign(redemptionResult);
                    if (!deal.id) {
                        void this.notificationService.displayAlert(null, "woa.message.loyalty-cards.redeemedDealNotAvailable");
                        return;
                    }

                    const result: DealResult = await this.dealModel.orderDeal(deal);
                    if (result.resultCode === DealResultCode.UNAVAILABLE) {
                        await this.notificationService.displayAlert(null, result.msg);
                    } else if (result.resultCode === DealResultCode.IMMEDIATE || result.resultCode === DealResultCode.DEFERRED) {
                        void this.dismiss();
                    }
                } else {
                    this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.message.loyalty-cards.dealRedeemed"));
                    void this.dismiss();
                }
            }
        }
    }
}
